// ─────────────────────────────────────────────
//  TimelineLightPage — the day, for the person working it.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  Who lands here: CRNAs, AAs and residents (HomePage.timelineRouteFor). The
//  full TimelineChartPage is an EDITING surface — it moves cases, restaffs rooms
//  and writes assignments — so this is its own route rather than the same page
//  with the controls hidden. A hidden control is one bug from reachable.
//
//  NOT A SMALLER MANAGER'S CHART. The full chart answers "where can I move a
//  case". The person reading THIS one has a different set of questions, and they
//  are in the order the page answers them:
//
//    1. Where am I, until when, and who is supervising me?   → the "your day" strip
//    2. What does the whole day look like?                    → the chart
//    3. When is my break, who is free, who do I ring?         → the side cards
//
//  Every number comes from `useControlPanelData` — the SAME hook the control
//  panel and the full timeline use, with the same arguments. Nothing here
//  re-derives a fact, so this page cannot disagree with the board a charge nurse
//  is reading two rooms away.
//
//  ⚠️ THERE IS NO WRITE PATH ON THIS PAGE. Not a disabled one — none. If you
//  add an action here, you have removed the reason the route exists.
//
//  ⚠️ Anything alert-derived (break state) is gated on `alertsCovered`: the
//  alert bus is scoped to the ACCOUNT's facility and, pointed elsewhere, is
//  wrong rather than empty. See LightCards.AlertsElsewhere.
// ─────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useDayKey } from "../hooks/useDayKey";
import { useIsMobile } from "../hooks/useIsMobile";
import useControlPanelData from "../hooks/useControlPanelData";
import { myRoomAssignments } from "../domain/myRooms";
import { roomForOrRoom } from "../domain/gridTemplate";
import { to12h } from "../domain/timeline";
import LightChart from "../components/timelineLight/LightChart";
import { OnCallCard, MdCallCard, FreeNowCard } from "../components/timelineLight/LightCards";
import { Avatar, SignOutButton } from "../components";
import NavMenu from "../components/NavMenu";
import tokens from "../theme/tokens";

const { color, space, radius, font } = tokens;
const { lobby } = color;

// "Tuesday, August 18" from a YYYY-MM-DD key.
//
// ⚠️ Parsed by hand: `new Date("2026-08-18")` is UTC and prints the 17th in
// Florida, so the header would disagree with the board underneath it.
export function formatDayKey(key) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ""));
  if (!m) return "";
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
}

// The rooms this viewer is on today, as ids.
//
// Uses the SAME role-aware matcher My Rooms uses (`myRoomAssignments`), which
// bridges the uid↔staffId gap. Matching on uid alone is what used to print
// "Not assigned yet" to people who were plainly on the board.
export function myRoomIdSet(assignments, user, staff, rooms) {
  const ids = new Set();
  for (const a of myRoomAssignments(assignments, user, staff)) {
    const room = roomForOrRoom(a?.orRoom, rooms);
    if (room?.id) ids.add(room.id);
  }
  return ids;
}

// The one-line answer to "where am I". Null when they are on no room, which is
// a real state (a bench day, a resident between rotations) and gets its own copy
// rather than an empty strip.
export function myStanding(model, myIds, nowMin) {
  const cases = [];
  for (const sec of model?.sections || []) {
    for (const room of sec.rooms || []) {
      if (!myIds.has(room.id)) continue;
      for (const c of room.cases || []) cases.push({ ...c, roomName: room.name });
    }
  }
  if (!cases.length) return null;
  const running = cases.find((c) => nowMin >= c.startMin && nowMin < c.endMin);
  const next = cases.filter((c) => c.startMin > nowMin).sort((a, b) => a.startMin - b.startMin)[0];
  return { current: running || null, next: next || null, count: cases.length };
}

export default function TimelineLightPage() {
  const navigate = useNavigate();
  const { user, isDoctor } = useAuth();
  const facilityId = user?.activeFacilityId;
  const dateKey = useDayKey();

  // One clock for the whole page, so the chart's NOW line and the "your day"
  // strip can never disagree about the time.
  const [nowMin, setNowMin] = useState(() => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  });
  useEffect(() => {
    const t = setInterval(() => {
      const d = new Date();
      setNowMin(d.getHours() * 60 + d.getMinutes());
    }, 30000);
    return () => clearInterval(t);
  }, []);

  const panel = useControlPanelData(facilityId, dateKey, { nowMin });
  const {
    model, rooms, assignments, staffProviders, mdCall, available, loading,
  } = panel;


  const [onlyMine, setOnlyMine] = useState(false);
  const [picked, setPicked] = useState(null);

  // ⚠️ ESCAPE CLOSES IT. This sheet had a ✕ button and a backdrop click and no
  // keyboard dismissal — a keyboard user who opened it could only leave by
  // tabbing to the ✕. Every other overlay in this app closes on Escape, so its
  // absence here was also an inconsistency people would not think to report.
  useEffect(() => {
    if (!picked) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setPicked(null); };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [picked]);
  // Below this the rail cannot sit beside the chart without squeezing both.
  const isNarrow = useIsMobile(1080);

  const myIds = useMemo(
    () => myRoomIdSet(assignments, user, staffProviders, rooms),
    [assignments, user, staffProviders, rooms]
  );
  const standing = useMemo(() => myStanding(model, myIds, nowMin), [model, myIds, nowMin]);

  return (
    <div style={s.page}>
      <div style={s.topbar}>
        <NavMenu />
        <div style={s.topbarRight}>
          {/* Dropped on a phone, where it crowded the facility tag — and where
              there is nothing on the page that could be mistaken for a control
              anyway. The badge earns its space on the desktop topbar. */}
          {!isNarrow && <span style={s.readOnly}>Read only</span>}
          <Avatar user={user} isDoctor={isDoctor} onClick={() => navigate("/profile")} />
          <SignOutButton />
        </div>
      </div>

      <main style={s.main}>
        <header style={s.head}>
          <div style={s.eyebrow}>{formatDayKey(dateKey)}</div>
          <h1 style={s.title}>Your day</h1>
          <span style={s.rule} aria-hidden="true" />
        </header>

        {/* ── WHERE AM I ── */}
        <section style={s.standing} aria-label="Where you are today">
          {loading ? (
            <p style={s.standingQuiet}>Reading today&apos;s board…</p>
          ) : !standing ? (
            <p style={s.standingQuiet}>
              You&apos;re not on a room today. The whole day is below.
            </p>
          ) : (
            <div style={s.standingRow}>
              {standing.current ? (
                <>
                  <div style={s.standingBlock}>
                    <span style={s.standingLabel}>Right now</span>
                    <span style={s.standingRoom}>{standing.current.roomName}</span>
                    <span style={s.standingMeta}>
                      until {standing.current.end12h}
                      {standing.current.attending ? ` · ⚕ ${standing.current.attending}` : ""}
                    </span>
                  </div>
                  {standing.current.depart12h && (
                    <div style={s.standingBlock}>
                      <span style={s.standingLabel}>You leave</span>
                      <span style={s.standingRoom}>{standing.current.depart12h}</span>
                    </div>
                  )}
                </>
              ) : (
                <div style={s.standingBlock}>
                  <span style={s.standingLabel}>Right now</span>
                  <span style={s.standingRoom}>Between cases</span>
                </div>
              )}
              {standing.next && (
                <div style={s.standingBlock}>
                  <span style={s.standingLabel}>Next</span>
                  <span style={s.standingRoom}>{standing.next.roomName}</span>
                  <span style={s.standingMeta}>from {standing.next.start12h}</span>
                </div>
              )}
            </div>
          )}
        </section>

        <div style={{ ...s.layout, ...(isNarrow ? s.layoutStacked : s.layoutWide) }}>
          {/* ── THE DAY ── */}
          <section style={s.chartCard} aria-label="The day's cases">
            <div style={s.chartHead}>
              <div>
                <span style={s.cardTitle}>The board</span>
                <div style={s.chartMeta}>
                  {model.occupiedCount} rooms · {model.caseCount} cases
                </div>
              </div>
              <label style={s.toggle}>
                <input
                  type="checkbox"
                  checked={onlyMine}
                  onChange={(e) => setOnlyMine(e.target.checked)}
                  disabled={!myIds.size}
                />
                <span>Only my rooms</span>
              </label>
            </div>
            <span style={s.cardRule} aria-hidden="true" />
            {/* The chart is wide by nature — it scrolls INSIDE this box so the
                page itself never scrolls sideways on a phone. */}
            <div style={s.chartScroll}>
              <LightChart
                model={model}
                nowMin={nowMin}
                myRoomIds={myIds}
                onlyMine={onlyMine}
                onPickCase={setPicked}
              />
            </div>
          </section>

          {/* ── WHO ── */}
          {/* ⚠️ NO BREAK LIST HERE (client, 2026-08-19). It was a full roster of
              who had taken which break, which is the BREAK BOARD's job — this
              page is the day's cases and who to reach. `BreaksCard` still
              exists and is still tested; it is simply not mounted on this
              surface. Same decision on the phone (MobileTimeline). */}
          <aside style={s.rail} aria-label="Today's team">
            <OnCallCard watches={model.onCallWatch} />
            <MdCallCard rows={mdCall} />
            <FreeNowCard providers={available?.providers} />
          </aside>
        </div>
      </main>

      {/* Case detail — the things you would otherwise have to ask somebody. */}
      {picked && (
        // The WRAPPER is the backdrop — decorative, click-to-close, with Escape
        // above as the keyboard equivalent. The dialog role belongs on the sheet
        // itself, which is the thing a screen reader should land in.
        <div style={s.sheetWrap} role="presentation" onClick={() => setPicked(null)}>
          {/* Not an interaction, a guard: cancels the backdrop's handler so a click
              inside the sheet does not close it. Escape is the keyboard path. */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          {/* Same guard: cancels the backdrop's close. Escape closes this sheet. */}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
          <div
            style={s.sheet}
            role="dialog"
            aria-modal="true"
            aria-label={`${picked.roomName} case`}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={s.sheetHead}>
              <div>
                <div style={s.sheetEyebrow}>{picked.sectionLabel}</div>
                <div style={s.sheetRoom}>{picked.roomName}</div>
              </div>
              <button type="button" style={s.sheetClose} onClick={() => setPicked(null)} aria-label="Close">✕</button>
            </div>
            <span style={s.cardRule} aria-hidden="true" />
            <dl style={s.dl}>
              <Row label="Runs" value={`${picked.start12h} – ${picked.end12h}`} />
              {/* ⚠️ `label`, NOT `provider`. `provider` is the raw cell, which on
                  an orienting row names TWO people in one string — the blob the
                  owner reported. `label` is the person holding the room; the
                  second person gets their own row, with their own leaving
                  time, because they leave when they leave. */}
              <Row label="Provider" value={picked.label || picked.provider || "—"} />
              {picked.depart12h && <Row label="Provider leaves" value={picked.depart12h} />}
              {picked.orientee && (
                <>
                  <Row label="Orienting" value={picked.orientee.name} />
                  {picked.orientee.depart12h && (
                    <Row label="Orientee leaves" value={picked.orientee.depart12h} />
                  )}
                </>
              )}
              <Row label="Supervising MD" value={picked.attending || "Not assigned"} />
              <Row label="Surgeon" value={picked.surgeon || "—"} />
              <Row label="Procedure" value={picked.procedure || "—"} />
              {picked.onCall && <Row label="Seat" value="On call" />}
            </dl>
            <p style={s.sheetNote}>
              Read only. Times and staffing are changed on the board by the AIC.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={s.dlRow}>
      <dt style={s.dt}>{label}</dt>
      <dd style={s.dd}>{value}</dd>
    </div>
  );
}

const s = {
  page: {
    minHeight: "100vh",
    background: lobby.linen,
    color: color.text.primary,
    fontFamily: font.family,
    display: "flex",
    flexDirection: "column",
  },
  topbar: {
    background: lobby.plaque,
    borderBottom: `1px solid ${lobby.sand}`,
    padding: "0 20px",
    height: 56,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
    position: "sticky",
    top: 0,
    zIndex: 20,
  },
  topbarRight: { display: "flex", alignItems: "center", gap: space.sm },
  readOnly: {
    fontSize: 9,
    fontWeight: font.weight.bold,
    letterSpacing: "1.2px",
    textTransform: "uppercase",
    color: lobby.brassInk,
    border: `1px solid ${lobby.brass}`,
    borderRadius: radius.full,
    padding: "2px 8px",
  },

  main: {
    flex: 1,
    width: "100%",
    maxWidth: 1280,
    margin: "0 auto",
    padding: `${space.lg}px ${space.lg}px ${space["2xl"]}px`,
    boxSizing: "border-box",
    minWidth: 0,
  },
  head: { marginBottom: space.md },
  eyebrow: {
    fontSize: 11,
    fontWeight: font.weight.bold,
    letterSpacing: "2.4px",
    textTransform: "uppercase",
    color: lobby.brassInk,
  },
  title: {
    margin: `${space.xs}px 0 0`,
    fontSize: 26,
    fontWeight: font.weight.semibold,
    letterSpacing: "-0.3px",
    lineHeight: font.lineHeight.tight,
  },
  rule: { display: "block", width: 56, height: 2, background: lobby.brass, borderRadius: radius.full, marginTop: space.sm },

  standing: {
    background: lobby.plaque,
    border: `1px solid ${lobby.sand}`,
    borderRadius: radius.xl,
    padding: `${space.md}px ${space.lg}px`,
    marginBottom: space.lg,
  },
  standingRow: { display: "flex", flexWrap: "wrap", gap: space.xl },
  standingBlock: { display: "flex", flexDirection: "column", minWidth: 0 },
  standingLabel: {
    fontSize: 10,
    fontWeight: font.weight.bold,
    letterSpacing: "1.4px",
    textTransform: "uppercase",
    color: lobby.brassInk,
  },
  standingRoom: { fontSize: 20, fontWeight: font.weight.bold, lineHeight: font.lineHeight.tight },
  standingMeta: { fontSize: font.size.label, color: color.text.secondary },
  standingQuiet: { margin: 0, fontSize: font.size.body, color: color.text.secondary },

  layout: {
    display: "grid",
    gap: space.lg,
    alignItems: "start",
  },
  // Chart + a fixed rail on a wide screen, stacked on a narrow one.
  //
  // ⚠️ THIS WAS `repeat(auto-fit, minmax(min(100%, 640px), 1fr))` AND IT SILENTLY
  // GAVE ONE COLUMN: two 640px tracks plus the gap need 1304px, and the measure
  // here is 1232 (1280 cap − 2×24 padding). auto-fit does not warn, it just
  // drops to a single column and the whole rail slides under the chart. An
  // explicit rail width is honest about what this layout is.
  //
  // minmax(0, 1fr) on the chart column is load-bearing: a plain `1fr` refuses to
  // shrink below its wide content and pushes the rail off the page.
  layoutWide: { gridTemplateColumns: "minmax(0, 1fr) 320px" },
  layoutStacked: { gridTemplateColumns: "minmax(0, 1fr)" },
  chartCard: {
    background: lobby.plaque,
    border: `1px solid ${lobby.sand}`,
    borderRadius: radius.xl,
    padding: space.md,
    minWidth: 0,
  },
  chartHead: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: space.md },
  cardTitle: {
    fontSize: 10,
    fontWeight: font.weight.bold,
    letterSpacing: "1.6px",
    textTransform: "uppercase",
    color: lobby.brassInk,
  },
  cardRule: { display: "block", height: 1, background: lobby.brass, opacity: 0.5, margin: `6px 0 ${space.md}px` },
  chartMeta: { fontSize: font.size.label, color: color.text.secondary, marginTop: 2 },
  toggle: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: font.size.label,
    color: color.text.secondary,
    whiteSpace: "nowrap",
    cursor: "pointer",
    minHeight: 32,
  },
  chartScroll: { overflowX: "auto", overflowY: "hidden", paddingBottom: space.sm },

  rail: { minWidth: 0 },

  sheetWrap: {
    position: "fixed",
    inset: 0,
    background: "rgba(13,15,26,.42)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: space.lg,
    zIndex: 100,
  },
  sheet: {
    background: lobby.plaque,
    border: `1px solid ${lobby.sand}`,
    borderRadius: radius.xl,
    padding: space.lg,
    width: "100%",
    maxWidth: 420,
    maxHeight: "80vh",
    overflowY: "auto",
  },
  sheetHead: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: space.md },
  sheetEyebrow: {
    fontSize: 10,
    fontWeight: font.weight.bold,
    letterSpacing: "1.4px",
    textTransform: "uppercase",
    color: lobby.brassInk,
  },
  sheetRoom: { fontSize: font.size.display, fontWeight: font.weight.bold, lineHeight: font.lineHeight.tight },
  sheetClose: {
    background: "transparent",
    border: "none",
    fontSize: 16,
    color: color.text.muted,
    cursor: "pointer",
    minWidth: 44,
    minHeight: 44,
  },
  dl: { margin: 0 },
  dlRow: { display: "flex", gap: space.md, padding: "7px 0", borderBottom: `1px solid ${lobby.sand}` },
  dt: {
    flex: "0 0 128px",
    fontSize: 10,
    fontWeight: font.weight.bold,
    letterSpacing: "1px",
    textTransform: "uppercase",
    color: color.text.muted,
    paddingTop: 2,
  },
  dd: { margin: 0, flex: 1, minWidth: 0, fontSize: font.size.body, fontWeight: font.weight.semibold },
  sheetNote: { margin: `${space.md}px 0 0`, fontSize: font.size.label, color: color.text.muted, lineHeight: font.lineHeight.normal },
};
